import { getRouteApi } from '@tanstack/react-router'
import { Suspense, useCallback, useMemo } from 'react'
import { z } from 'zod'

import type { SkelEditorSearch } from '@/features/skel/SkelEditor'
import { lazySkelPage, lenientSkel, SkelPageLoading } from '@/routes/skel.index'

/**
 * `/skel/$id?view=&anim=&edit=` — the skeleton editor (docs/SKEL-EDITOR.md §5.4).
 *
 * - `view`: a key of the project's `views` (`front`, `side`, `main`, …).
 * - `anim`: an animation of that view, by NAME (names survive re-ordering; indices do not).
 * - `edit`: `rig` (default) | `animate` — the editor's mode. The spec calls it `?mode=`, but that
 *   key is taken by `/annotate` (`simple | expert`): TanStack types `to: '.'` search reducers
 *   against the union of every route's params, so a second `mode` type breaks the annotator's
 *   typecheck. The editor itself still speaks `mode`; only the URL key differs.
 *
 * The editor applies them once the project is loaded and rewrites them in place (replace) as the
 * user switches tabs, so the address bar is always a shareable deep link. Unknown values are
 * dropped by the lenient schema; values that do not match the project are ignored by the editor.
 *
 * The editor is keyed by the id: opening another project remounts it, so the store resets before
 * the next one loads. Nothing is saved on the way out: the editor saves only on Save, and its
 * `SkelLeaveGuard` (inside the lazily loaded editor, so the blocker ships with it) asks first
 * when there are unsaved changes — Save and leave / Leave without saving / Stay — plus the
 * browser's own prompt on closing the tab. A `?view=` / `?anim=` / `?edit=` change never asks.
 */

export const SKEL_MODES = ['rig', 'animate'] as const

export const skelEditorSearchSchema = z.object({
  view: lenientSkel(z.string().regex(/^[A-Za-z0-9_-]{1,64}$/)),
  // A hand-typed `?anim=12` reaches us as the NUMBER 12 (the router JSON-parses search values);
  // an animation may well be called "12", so a finite number is read back as its name.
  anim: lenientSkel(z.union([z.string(), z.number().finite().transform(String)]).pipe(z.string().min(1).max(200))),
  edit: lenientSkel(z.enum(SKEL_MODES)),
})
export type SkelEditorRouteSearch = z.infer<typeof skelEditorSearchSchema>

export function validateSkelEditorSearch(input: Record<string, unknown>): SkelEditorRouteSearch {
  return skelEditorSearchSchema.parse(input)
}

export const SKEL_EDITOR_ROUTE_ID = '/skel/$id' as const
const route = getRouteApi(SKEL_EDITOR_ROUTE_ID)

const SkelEditor = lazySkelPage(() => import('@/features/skel/SkelEditor'), 'skeleton editor')

export function SkelEditorPage() {
  const { id } = route.useParams()
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const onSearchChange = useCallback(
    (next: SkelEditorSearch) => void navigate({ search: (prev) => ({ ...prev, view: next.view, anim: next.anim, edit: next.mode }), replace: true }),
    [navigate],
  )
  const editorSearch = useMemo<SkelEditorSearch>(() => ({ view: search.view, anim: search.anim, mode: search.edit }), [search.view, search.anim, search.edit])
  return (
    <Suspense fallback={<SkelPageLoading label="Opening the skeleton editor…" />}>
      <SkelEditor key={id} projectId={id} search={editorSearch} onSearchChange={onSearchChange} />
    </Suspense>
  )
}
